import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../App';
import { ChevronDown, Trophy, Star, Award, ArrowLeft } from 'lucide-react';

const PokerHandsGuide = () => {
  const navigate = useNavigate();
  const [expandedHand, setExpandedHand] = useState(null);

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/');
    }
  };

  const toggleHand = (index) => {
    setExpandedHand(expandedHand === index ? null : index);
  };

  const pokerHands = [
    {
      rank: 1,
      name: "ROYAL FLUSH",
      description: "The highest possible hand. A, K, Q, J, 10 all in the same suit.",
      example: "A♠ K♠ Q♠ J♠ 10♠",
      explanation: "The absolute best hand in poker. All five highest cards in one suit. Extremely rare and unbeatable.",
      color: "from-amber-600 to-amber-800",
      icon: Trophy
    },
    {
      rank: 2,
      name: "STRAIGHT FLUSH",
      description: "Five consecutive cards of the same suit.",
      example: "9♦ 8♦ 7♦ 6♦ 5♦",
      explanation: "Five cards in numerical order, all the same suit. Second strongest hand in poker.",
      color: "from-purple-600 to-purple-800",
      icon: Star
    },
    {
      rank: 3,
      name: "FOUR OF A KIND",
      description: "Four cards of the same rank.",
      example: "K♣ K♦ K♥ K♠ + any card",
      explanation: "Also called 'Quads'. Four cards with the same number or face. Very strong hand.",
      color: "from-red-600 to-red-800",
      icon: Award
    },
    {
      rank: 4,
      name: "FULL HOUSE",
      description: "Three of a kind plus a pair.",
      example: "Q♠ Q♦ Q♣ + 8♥ 8♠",
      explanation: "Three matching cards and two other matching cards. Also called a 'Boat' or 'Full Boat'.",
      color: "from-blue-600 to-blue-800",
      icon: Award
    },
    {
      rank: 5,
      name: "FLUSH",
      description: "Five cards of the same suit (not in sequence).",
      example: "K♥ 10♥ 7♥ 5♥ 2♥",
      explanation: "Any five cards of the same suit. If multiple players have a flush, highest card wins.",
      color: "from-emerald-600 to-emerald-800",
      icon: Award
    },
    {
      rank: 6,
      name: "STRAIGHT",
      description: "Five consecutive cards (mixed suits).",
      example: "10♣ 9♦ 8♠ 7♥ 6♣",
      explanation: "Five cards in numerical order, but different suits. Ace can be high or low.",
      color: "from-cyan-600 to-cyan-800",
      icon: Award
    },
    {
      rank: 7,
      name: "THREE OF A KIND",
      description: "Three cards of the same rank.",
      example: "7♠ 7♦ 7♣ + K♥ + 4♠",
      explanation: "Also called 'Trips' or 'Set'. Three cards with the same number or face.",
      color: "from-indigo-600 to-indigo-800",
      icon: Award
    },
    {
      rank: 8,
      name: "TWO PAIR",
      description: "Two different pairs.",
      example: "J♠ J♦ + 4♣ 4♥ + A♠",
      explanation: "Two separate pairs of matching cards. The highest pair determines winner if tied.",
      color: "from-violet-600 to-violet-800",
      icon: Award
    },
    {
      rank: 9,
      name: "ONE PAIR",
      description: "Two cards of the same rank.",
      example: "A♠ A♦ + K♣ + 9♥ + 5♠",
      explanation: "Two matching cards. Most common winning hand in poker. Higher pair wins.",
      color: "from-pink-600 to-pink-800",
      icon: Award
    },
    {
      rank: 10,
      name: "HIGH CARD",
      description: "No matching cards — highest card plays.",
      example: "A♠ + K♦ + 8♣ + 5♥ + 2♠",
      explanation: "When you have nothing, your highest card determines your hand strength.",
      color: "from-slate-600 to-slate-800",
      icon: Award
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0F14] via-[#0A241C] to-[#071A14]">
      <Navigation />
      
      {/* Poker Hands List */}
      <div className="max-w-4xl mx-auto px-4 pt-4 pb-12">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-6 group"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">Back</span>
        </button>
        
        <div className="space-y-4">
          {pokerHands.map((hand, index) => {
            const Icon = hand.icon;
            const isExpanded = expandedHand === index;

            return (
              <div
                key={index}
                className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border-2 border-[#2D2D2D] rounded-2xl overflow-hidden transition-all duration-300 hover:border-[#C9A227]/40 shadow-lg"
              >
                {/* Accordion Header */}
                <button
                  onClick={() => toggleHand(index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left transition-all duration-200 hover:bg-[#1A1A1A]/50"
                >
                  <div className="flex items-center gap-4 flex-1">
                    {/* Rank Badge */}
                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${hand.color} flex items-center justify-center shadow-lg`}>
                      <span className="text-white font-bold text-lg">#{hand.rank}</span>
                    </div>

                    {/* Hand Name and Description */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-xl md:text-2xl font-bold text-white font-heading">
                          {hand.name}
                        </h3>
                        {hand.rank === 1 && (
                          <Trophy className="w-5 h-5 text-[#C9A227]" />
                        )}
                      </div>
                      <p className="text-zinc-400 text-sm md:text-base">
                        {hand.description}
                      </p>
                    </div>
                  </div>

                  {/* Expand Icon */}
                  <ChevronDown
                    className={`w-6 h-6 text-[#C9A227] transition-transform duration-300 flex-shrink-0 ml-4 ${
                      isExpanded ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Accordion Content */}
                <div
                  className={`transition-all duration-300 ease-in-out ${
                    isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  } overflow-hidden`}
                >
                  <div className="px-6 pb-6 pt-2 border-t border-[#2D2D2D]">
                    {/* Example Section */}
                    <div className="bg-[#0D0D0D] border border-[#2D2D2D] rounded-xl p-5">
                      <div className="mb-3">
                        <span className="text-[#C9A227] font-semibold text-sm uppercase tracking-wide">
                          Example
                        </span>
                      </div>
                      
                      {/* Card Display */}
                      <div className="mb-4">
                        <div className="flex flex-wrap items-center gap-3">
                          {hand.example.split(' ').map((card, idx) => (
                            <div
                              key={idx}
                              className="bg-white text-black px-4 py-3 rounded-lg font-bold text-lg md:text-xl shadow-md border-2 border-zinc-300"
                              style={{ minWidth: '60px', textAlign: 'center' }}
                            >
                              {card}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Explanation */}
                      <div className="bg-[#1A1A1A]/50 rounded-lg p-4 border border-[#2D2D2D]">
                        <p className="text-zinc-300 leading-relaxed">
                          {hand.explanation}
                        </p>
                      </div>
                    </div>

                    {/* Tap to close hint */}
                    <div className="mt-4 text-center">
                      <span className="text-zinc-600 text-xs">
                        Tap to close
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Tips */}
        <div className="mt-12 bg-gradient-to-br from-[#0D3D2C] to-[#0A241C] border-2 border-[#C9A227]/20 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Star className="w-6 h-6 text-[#C9A227]" />
            Quick Tips
          </h3>
          <ul className="space-y-3 text-zinc-300">
            <li className="flex items-start gap-3">
              <span className="text-[#C9A227] mt-1">•</span>
              <span>The higher the hand on this list, the stronger it is</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#C9A227] mt-1">•</span>
              <span>When two players have the same hand type, the highest cards determine the winner</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#C9A227] mt-1">•</span>
              <span>In Texas Hold'em, you make your best 5-card hand from your 2 hole cards + 5 community cards</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#C9A227] mt-1">•</span>
              <span>Suits are equal in poker — no suit is stronger than another</span>
            </li>
          </ul>
        </div>

        {/* Call to Action */}
        <div className="mt-8 text-center">
          <a
            href="/play"
            className="inline-block bg-gradient-to-r from-[#C9A227] to-[#E1C25B] text-[#0B0F14] px-8 py-4 rounded-xl font-bold text-lg hover:shadow-[0_0_30px_rgba(201,162,39,0.4)] transition-all duration-300"
          >
            Practice With Live Hands →
          </a>
        </div>
      </div>
    </div>
  );
};

export default PokerHandsGuide;
